import Axios from 'axios'

/**
 * Número máximo de reintentos para endpoints críticos
 * Garantiza cobertura de 52.5s en peor caso (ventana de muerte de 50s)
 */
export const RESILIENT_MAX_RETRIES = 6

/**
 * Calcula el delay de backoff exponencial con jitter para endpoints críticos
 * Diseñado para cubrir la "ventana de muerte" de 50s durante reinicios de pods
 *
 * Estrategia:
 * - Backoff exponencial: baseDelay × 2^attemptNumber
 * - Cap máximo: 30s (petición de Backend para evitar delays muy largos)
 * - Jitter multiplicativo: 50-100% del valor base (distribuye carga y previene thundering herd)
 * - Total de reintentos: RESILIENT_MAX_RETRIES (6) para garantizar cobertura en el peor caso
 *
 * Curva de delays PEOR CASO (jitter × 0.5 - garantía SRE):
 * - Intento 0: 1.5s    → Acumulado: 1.5s
 * - Intento 1: 3s      → Acumulado: 4.5s
 * - Intento 2: 6s      → Acumulado: 10.5s
 * - Intento 3: 12s     → Acumulado: 22.5s
 * - Intento 4: 15s     → Acumulado: 37.5s
 * - Intento 5: 15s     → Acumulado: 52.5s ✓ (cubre los 50s incluso en peor caso)
 *
 * Curva de delays PROMEDIO (jitter × 0.75):
 * - Intento 0: ~2.25s  → Acumulado: ~2.25s
 * - Intento 1: ~4.5s   → Acumulado: ~6.75s
 * - Intento 2: ~9s     → Acumulado: ~15.75s
 * - Intento 3: ~18s    → Acumulado: ~33.75s
 * - Intento 4: ~22.5s  → Acumulado: ~56.25s
 * - Intento 5: ~22.5s  → Acumulado: ~78.75s ✓ (amplio margen de seguridad)
 *
 * @param attemptNumber - Número de intento actual (0-indexed)
 * @param baseDelay - Delay base en ms (default: 3000ms, cercano al p95 de 2.3s)
 * @param maxDelay - Cap máximo de delay en ms (default: 30000ms)
 * @returns Delay en milisegundos con jitter aplicado
 *
 * @example
 * // Uso en TanStack Query
 * retryDelay: (attemptIndex) => calculateResilientBackoff(attemptIndex)
 */
export const calculateResilientBackoff = (
    attemptNumber: number,
    baseDelay: number = 3000,
    maxDelay: number = 30000
): number => {
    // Backoff exponencial con cap
    const exponentialDelay = Math.min(baseDelay * Math.pow(2, attemptNumber), maxDelay)

    // Jitter multiplicativo: 50-100% del valor base
    // Esto distribuye las requests en una ventana de tiempo para prevenir thundering herd
    const jitterMultiplier = 0.5 + Math.random() * 0.5

    return Math.floor(exponentialDelay * jitterMultiplier)
}

/**
 * @param maxSeconds - Ventana máxima de distribución en segundos (default: 180s = 3 minutos)
 * @returns Delay en milisegundos (0 a maxSeconds × 1000)
 *
 * @example
 * // Uso en React Query con enabled delay
 * const delay = getInitialJitter(180)
 * setTimeout(() => setEnabled(true), delay)
 */
export const getInitialJitter = (maxSeconds: number = 180): number => {
    // Distribución uniforme: 0 a maxSeconds
    return Math.floor(Math.random() * maxSeconds * 1000)
}

/**
 * Predicado reutilizable para decidir si reintentar una request fallida
 * Diseñado para endpoints críticos que necesitan sobrevivir reinicios de pods
 *
 * Estrategia de detección de errores:
 * 1. Reintentar errores de red recuperables (Connection Refused, Connection Reset)
 *    - Ocurren cuando el pod está reiniciando o Nginx está caído
 * 2. Reintentar errores HTTP 5xx (502 Bad Gateway, 503 Service Unavailable, 521, etc)
 *    - Errores del servidor, no del cliente
 * 3. NO reintentar errores 4xx (401 Unauthorized, 403 Forbidden, 404 Not Found)
 *    - Errores del cliente que no se resolverán con reintentos
 * 4. NO reintentar errores de red no recuperables (CORS, DNS, Network Unreachable)
 *    - No se resolverán con reintentos y solo demoran la UX
 *
 * @param failureCount - Número de fallos acumulados (1-indexed, comienza en 1)
 * @param error - Error capturado
 * @returns true si debe reintentar, false si debe fallar definitivamente
 *
 * @example
 * // Uso en TanStack Query
 * retry: shouldRetryResilient
 */
export const shouldRetryResilient = (failureCount: number, error: unknown): boolean => {
    // Límite de RESILIENT_MAX_RETRIES reintentos (garantiza 52.5s en peor caso de jitter)
    // failureCount en TanStack Query v5 es 1-indexed (primer fallo = 1)
    // Para RESILIENT_MAX_RETRIES=6: permitir failureCount 1, 2, 3, 4, 5, 6
    if (failureCount > RESILIENT_MAX_RETRIES) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('[Resilience] Max retries reached', { maxRetries: RESILIENT_MAX_RETRIES, failureCount, error })
        }
        return false
    }

    // Caso 1: Error de Red sin response
    if (Axios.isAxiosError(error) && !error.response) {
        // En navegadores, por seguridad, los códigos específicos (ECONNREFUSED, etc) son ocultados
        // Axios devuelve un genérico "Network Error" sin error.code
        // Estrategia: Asumir que errores de red sin response son recuperables (pod reiniciando, Nginx caído)
        //
        // Deliberadamente aceptamos reintentar errores de CORS (que indican una mala
        // configuración y no se resolverán con reintentos),
        // ya que son muy raros en producción. Esta es una decisión pragmática: preferimos
        // incurrir en reintentos innecesarios en estos casos
        // para asegurarnos de que los errores de red legítimos (como caídas de pods o
        // Nginx) sí sean reintentados, ya que en el navegador
        // no es posible distinguir entre ambos tipos de error.

        if (process.env.NODE_ENV === 'development') {
            console.info('[Resilience] Network error (no response), retrying', {
                failureCount,
                errorCode: error.code || 'NO_CODE',
                message: error.message,
            })
        }

        // Reintentar TODOS los errores de red sin response
        // Esto cubre: Connection Refused, Connection Reset, Timeouts, y Network Errors genéricos
        return true
    }

    // Caso 2: Error HTTP con response
    if (Axios.isAxiosError(error) && error.response) {
        const status = error.response.status

        // Reintentar solo en errores 5xx (502 Bad Gateway, 503 Service Unavailable, 521, etc)
        if (status >= 500 && status < 600) {
            if (process.env.NODE_ENV === 'development') {
                console.info('[Resilience] Server error (5xx), retrying', { failureCount, status })
            }
            return true
        }

        // NO reintentar en 4xx (401 Unauthorized, 403 Forbidden, 404 Not Found, etc)
        if (process.env.NODE_ENV === 'development') {
            console.warn('[Resilience] Client error (4xx), not retrying', { failureCount, status })
        }
        return false
    }

    // Otros tipos de error: no reintentar
    if (process.env.NODE_ENV === 'development') {
        console.warn('[Resilience] Unknown error type, not retrying', { failureCount, error })
    }
    return false
}
